import cachedHttpClient from '../cached-http-client';
import type {
  EventAdminMetricsResponseDto,
  OrganizerMetricsResponseDto,
  OrganizerGetAnalyticsResponse,
} from '../../api/generated/types.gen';
import { EventRole } from '../../types/enums/event-roles.enum';

/** Роли управления — подмножество EventRole, только management-роли */
export type ManagementRole =
  | EventRole.ORGANIZER
  | EventRole.CO_ORGANIZER
  | EventRole.EVENT_ADMIN;

export type EventReportType =
  | 'participants'
  | 'engagement'
  | 'revenue'
  | 'content'
  | 'moderation'
  | 'notifications'
  | 'team_performance'
  | 'event_performance'    // 5.2 — ORGANIZER only; комплексный анализ всего события
  | 'participant_analysis'; // 5.2 — все management-роли; детальная сегментация участников

/** Типы, доступные только ORGANIZER (не CO_ORGANIZER, не EVENT_ADMIN) */
export const ORGANIZER_ONLY_REPORT_TYPES = new Set<EventReportType>(['event_performance']);

export type EventReportFormat = 'pdf' | 'excel' | 'csv' | 'json';
export type EventReportPeriod = 'daily' | 'weekly' | 'monthly' | 'custom';

export interface GenerateEventReportParams {
  eventId: string;
  reportType: EventReportType;
  format?: EventReportFormat;
  period?: EventReportPeriod;
  startDate?: string;
  endDate?: string;
  includeCharts?: boolean;
  includeDetails?: boolean;
}

export interface EventReportResponse {
  success: boolean;
  reportId: string;
  reportName: string;
  reportType: EventReportType;
  format: EventReportFormat;
  status: 'generating' | 'completed' | 'failed';
  downloadUrl?: string;
  fileSize?: number;
  createdAt: string;
  completedAt?: string;
  message: string;
}

export interface EventQuickStats {
  success: boolean;
  data: {
    totalEvents: number;
    activeEvents: number;
    totalParticipants: number;
    activeParticipants: number;
    pendingModeration: number;
    sentNotifications: number;
    teamMembers: number;
    completedTasks: number;
    totalTasks: number;
    engagementRate: number;
    satisfactionScore: number;
    lastActivity: string;
  };
  message: string;
}

/**
 * Ответы метрик — из генерата OpenAPI, а не из самописного зеркала.
 *
 * AD-360: до 19.09.2026 здесь стоял один интерфейс `EventMetrics`
 * (`participantsCount`, `budgetTotal`, `budgetSpent`, `engagementRate`,
 * `tasksCompleted`, `tasksPending`, `notificationsSent`), которым были помечены
 * СРАЗУ ОБА эндпоинта. В контракте сервера таких полей нет ни у одного из них:
 * `/event-admin/metrics` отдаёт `EventAdminMetricsDataDto`, `/organizer/metrics`
 * — `OrganizerMetricsDto`, и это разные формы. Компилятор молчал (зеркало
 * объявляло форму, а не сверяло её), зато каждое чтение давало `undefined`,
 * который роутер аналитики превращал в ноль. Тип из генерата делает любое
 * расхождение с сервером ошибкой сборки.
 */
export type OrganizerMetrics = OrganizerMetricsResponseDto;
export type EventAdminMetrics = EventAdminMetricsResponseDto;

/** Ответ `/organizer/analytics` — из генерата; там все поля необязательные. */
export type EventAnalyticsData = OrganizerGetAnalyticsResponse;

/**
 * AD-686. «Quick Statistics» идут мимо кеша ответов `cachedHttpClient` (5 минут,
 * общий на вкладку). Свежестью этих чисел управляет React Query: отметка
 * посещаемости сбрасывает их ключ. Под вторым кешем перечитанный запрос получал
 * прежний ответ, и сброс ничего не давал. Транспорт тот же — экземпляр axios
 * клиента: адрес сервера и заголовок авторизации прежние.
 */
const getQuickStatsUncached = (url: string, eventId: string) =>
  cachedHttpClient.getClient().get<EventQuickStats>(url, {
    params: { eventId },
  });

/** API для ORGANIZER и CO_ORGANIZER */
export const organizerAnalyticsApi = {
  getMetrics: (eventId: string) =>
    cachedHttpClient.get<OrganizerMetrics>('/organizer/metrics', {
      params: { eventId },
    }),

  getAnalytics: (eventId: string) =>
    cachedHttpClient.get<EventAnalyticsData>('/organizer/analytics', {
      params: { eventId },
    }),

  getQuickStats: (eventId: string) =>
    getQuickStatsUncached('/organizer/reports/quick-stats', eventId),

  generateReport: (params: GenerateEventReportParams) =>
    cachedHttpClient.post<EventReportResponse>('/organizer/reports', {}, {
      params,
    }),

  getParticipants: (eventId: string) =>
    cachedHttpClient.get('/organizer/participants', {
      params: { eventId },
    }),

  getRecentEvents: (eventId: string, page = 1, limit = 10) =>
    cachedHttpClient.get('/organizer/events/recent', {
      params: { eventId, page, limit },
    }),

  getBudgetOverview: (eventId: string) =>
    cachedHttpClient.get('/organizer/budget/overview', {
      params: { eventId },
    }),
};

/** API для EVENT_ADMIN */
export const eventAdminAnalyticsApi = {
  getMetrics: (eventId: string) =>
    cachedHttpClient.get<EventAdminMetrics>('/event-admin/metrics', {
      params: { eventId },
    }),

  getQuickStats: (eventId: string) =>
    getQuickStatsUncached('/event-admin/reports/quick-stats', eventId),

  generateReport: (params: GenerateEventReportParams) =>
    cachedHttpClient.post<EventReportResponse>('/event-admin/reports', {}, {
      params,
    }),

  getParticipants: (eventId: string, page = 1, limit = 10) =>
    cachedHttpClient.get('/event-admin/participants', {
      params: { eventId, page, limit },
    }),
};

/** Единый фасад — роутит по переданной роли */
export const eventAnalyticsApi = {
  getQuickStats: (eventId: string, role: ManagementRole) => {
    if (role === EventRole.EVENT_ADMIN) {
      return eventAdminAnalyticsApi.getQuickStats(eventId);
    }
    return organizerAnalyticsApi.getQuickStats(eventId);
  },

  getMetrics: (eventId: string, role: ManagementRole) => {
    if (role === EventRole.EVENT_ADMIN) {
      return eventAdminAnalyticsApi.getMetrics(eventId);
    }
    return organizerAnalyticsApi.getMetrics(eventId);
  },

  generateReport: (params: GenerateEventReportParams, role: ManagementRole) => {
    if (role === EventRole.EVENT_ADMIN) {
      return eventAdminAnalyticsApi.generateReport(params);
    }
    return organizerAnalyticsApi.generateReport(params);
  },
};

export default eventAnalyticsApi;
